import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { moderatorLogin, moderatorVerifyTwoFactor } from "../../config/api";
import "./AuthPage.css";

const ModeratorLoginPage = () => {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 2FA state — `challengeToken` doubles as the view discriminator.
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  const handleBackClick = () => navigate("/");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);

    const nextErrors: { email?: string; password?: string } = {};

    if (!email.trim()) {
      nextErrors.email = t("shared.errors.enterEmail");
    }

    if (!password.trim()) {
      nextErrors.password = t("shared.errors.enterPassword");
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await moderatorLogin({ email: email.trim(), password });
      if (result.twoFactorRequired) {
        setChallengeToken(result.challengeToken);
        setCode("");
        setUseRecoveryCode(false);
        setTwoFactorError(null);
      } else {
        navigate("/moderator/dashboard");
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t("moderatorLogin.errors.signInFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifySubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTwoFactorError(null);

    const trimmed = code.trim();
    if (!trimmed) {
      setTwoFactorError(
        useRecoveryCode ? t("shared.twoFactor.errors.enterRecovery") : t("shared.twoFactor.errors.enterCode"),
      );
      return;
    }
    if (!challengeToken) {
      setTwoFactorError(t("shared.twoFactor.errors.sessionExpired"));
      return;
    }

    setIsVerifying(true);
    try {
      await moderatorVerifyTwoFactor(
        useRecoveryCode ? { challengeToken, recoveryCode: trimmed } : { challengeToken, code: trimmed },
      );
      navigate("/moderator/dashboard");
    } catch (err) {
      setTwoFactorError(err instanceof Error ? err.message : t("shared.twoFactor.errors.invalidCode"));
    } finally {
      setIsVerifying(false);
    }
  };

  const cancelTwoFactor = () => {
    setChallengeToken(null);
    setCode("");
    setUseRecoveryCode(false);
    setTwoFactorError(null);
  };

  return (
    <div className="auth-page">
      <div className="auth-page__background" />

      <div className="container">
        <div className="auth-page__inner">
          <button type="button" className="auth-page__back-link" onClick={handleBackClick}>
            {t("shared.backToPlatformSelection")}
          </button>

          <section className="auth-card" aria-label={t("moderatorLogin.ariaLabel")}>
            <div className="auth-card__icon-wrapper">
              <div className="auth-card__icon auth-card__icon--primary">
                <span className="auth-card__icon-glyph" aria-hidden="true">
                  <i className="fa-solid fa-user-shield" />
                </span>
              </div>
            </div>

            <header className="auth-card__header">
              <h1 className="auth-card__title">
                {challengeToken ? t("shared.twoFactor.title") : t("moderatorLogin.title")}
              </h1>
              <p className="auth-card__subtitle">
                {challengeToken
                  ? useRecoveryCode
                    ? t("shared.twoFactor.subtitleRecovery")
                    : t("shared.twoFactor.subtitleCode")
                  : t("moderatorLogin.subtitle")}
              </p>
            </header>

            {challengeToken ? (
              <form className="auth-form" onSubmit={handleVerifySubmit}>
                <div className="auth-form__field">
                  <label className="auth-form__label" htmlFor="moderator-2fa-code">
                    {useRecoveryCode ? t("shared.twoFactor.recoveryLabel") : t("shared.twoFactor.codeLabel")}
                  </label>
                  <div
                    className={`auth-form__input-wrapper ${twoFactorError ? "auth-form__input-wrapper--error" : ""}`}
                  >
                    <span className="auth-form__input-icon" aria-hidden="true">
                      <i className="fa-solid fa-shield-halved" />
                    </span>
                    <input
                      id="moderator-2fa-code"
                      type="text"
                      className="auth-form__input"
                      placeholder={useRecoveryCode ? "xxxx-xxxx" : "123456"}
                      inputMode={useRecoveryCode ? "text" : "numeric"}
                      autoComplete="one-time-code"
                      autoFocus
                      value={code}
                      onChange={(event) => setCode(event.target.value)}
                      required
                    />
                  </div>
                </div>

                {twoFactorError ? (
                  <p className="auth-form__error" role="alert">
                    {twoFactorError}
                  </p>
                ) : null}

                <button type="submit" className="auth-form__submit auth-form__submit--primary" disabled={isVerifying}>
                  {isVerifying ? t("shared.twoFactor.verifying") : t("shared.twoFactor.verify")}
                </button>

                <div className="auth-form__divider">
                  <span>{t("shared.or")}</span>
                </div>

                <button
                  type="button"
                  className="auth-form__link"
                  onClick={() => {
                    setUseRecoveryCode((prev) => !prev);
                    setCode("");
                    setTwoFactorError(null);
                  }}
                >
                  {useRecoveryCode ? t("shared.twoFactor.useAuthenticator") : t("shared.twoFactor.useRecovery")}
                </button>

                <p className="auth-form__footer-text">
                  <button type="button" className="auth-form__link auth-form__link--muted" onClick={cancelTwoFactor}>
                    {t("shared.backToSignIn")}
                  </button>
                </p>
              </form>
            ) : (
              <form className="auth-form" onSubmit={handleSubmit}>
                <div className="auth-form__field">
                  <label className="auth-form__label" htmlFor="moderator-email">
                    {t("shared.emailLabel")}
                  </label>
                  <div className={`auth-form__input-wrapper ${errors.email ? "auth-form__input-wrapper--error" : ""}`}>
                    <span className="auth-form__input-icon" aria-hidden="true">
                      <i className="fa-regular fa-envelope" />
                    </span>
                    <input
                      id="moderator-email"
                      type="email"
                      className="auth-form__input"
                      placeholder={t("shared.moderatorEmailPlaceholder")}
                      autoComplete="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      required
                    />
                  </div>
                  {errors.email ? <p className="auth-form__error">{errors.email}</p> : null}
                </div>

                <div className="auth-form__field">
                  <label className="auth-form__label" htmlFor="moderator-password">
                    {t("shared.passwordLabel")}
                  </label>
                  <div
                    className={`auth-form__input-wrapper auth-form__input-wrapper--password ${
                      errors.password ? "auth-form__input-wrapper--error" : ""
                    }`}
                  >
                    <span className="auth-form__input-icon" aria-hidden="true">
                      <i className="fa-solid fa-lock" />
                    </span>
                    <input
                      id="moderator-password"
                      type={showPassword ? "text" : "password"}
                      className="auth-form__input"
                      placeholder={t("shared.passwordPlaceholder")}
                      autoComplete="current-password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="auth-form__password-toggle"
                      aria-label={showPassword ? t("shared.hidePassword") : t("shared.showPassword")}
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      <i className={showPassword ? "fa-regular fa-eye-slash" : "fa-regular fa-eye"} />
                    </button>
                  </div>
                  {errors.password ? <p className="auth-form__error">{errors.password}</p> : null}
                </div>

                <div className="auth-form__meta-row">
                  <label className="auth-form__checkbox">
                    <input type="checkbox" />
                    <span>{t("shared.rememberMe")}</span>
                  </label>
                  <button
                    type="button"
                    className="auth-form__link auth-form__link--small"
                    onClick={() => navigate("/moderator/forgot-password")}
                  >
                    {t("shared.forgotPassword")}
                  </button>
                </div>

                {submitError ? (
                  <p className="auth-form__error" role="alert">
                    {submitError}
                  </p>
                ) : null}

                <button type="submit" className="auth-form__submit auth-form__submit--primary" disabled={isSubmitting}>
                  {isSubmitting ? t("shared.signingIn") : t("shared.signIn")}
                </button>

                {/* No sign up section for moderators */}
              </form>
            )}

            <p className="auth-page__legal-text auth-page__legal-text--muted">
              <Trans
                t={t}
                i18nKey="shared.legal"
                components={{
                  terms: <button type="button" className="auth-form__link auth-form__link--muted" />,
                  privacy: <button type="button" className="auth-form__link auth-form__link--muted" />,
                }}
              />
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ModeratorLoginPage;
